
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users,
  Building2,
  BriefcaseBusiness,
  ClipboardList,
  CircleDollarSign,
  CheckCircle,
  Download,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

const RANGES = {
  "7d": { label: "Last 7 days", days: 7 },
  "30d": { label: "Last 30 days", days: 30 },
  "90d": { label: "Last 90 days", days: 90 },
  "12m": { label: "Last 12 months", days: 365 },
};

const API_ENDPOINTS = {
  partners: "/api/partners/all",
  applications: "/api/job-applications",
  interviews: "/api/interviews",
  trainers: "/api/trainers/all",
  jobs: "/api/jobs",
};

const PALETTE = ["#3E0F8D", "#9564DD", "#E4DA72", "#B9A2E8", "#6B3FBF"];

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const fmtMoney = (n) => `₹${fmt(n)}`;

function getList(json) {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.content)) return json.content;
  if (Array.isArray(json?.content?.content)) return json.content.content;
  return [];
}

function getStatus(item) {
  return String(
    item?.status ??
    item?.applicationStatus ??
    item?.trainerStatus ??
    ""
  ).trim().toUpperCase();
}

function getDate(item) {
  const value =
    item?.appliedAt ??
    item?.createdAt ??
    item?.createdDate ??
    item?.postedAt;

  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function TrendChart({ labels, series }) {
  const W = 640;
  const H = 240;
  const P = { t: 16, r: 16, b: 28, l: 42 };
  const iw = W - P.l - P.r;
  const ih = H - P.t - P.b;

  const max = Math.max(
    1,
    ...series.flatMap((s) => s.data)
  ) * 1.15;

  const x = (i) =>
    P.l +
    (labels.length === 1 ? iw / 2 : (i / (labels.length - 1)) * iw);

  const y = (v) => P.t + ih - (v / max) * ih;

  const path = (values) =>
    values
      .map((v, i) => `${i ? "L" : "M"}${x(i)},${y(v)}`)
      .join(" ");

  const area = (values) =>
    values.length
      ? `${path(values)} L${x(values.length - 1)},${P.t + ih} L${x(0)},${P.t + ih} Z`
      : "";

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((v) =>
    Math.round(max * v)
  );

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="rp-svg"
      role="img"
      aria-label="Applications and placements trend"
    >
      {ticks.map((tick, i) => (
        <g key={`${tick}-${i}`}>
          <line
            x1={P.l}
            x2={W - P.r}
            y1={y(tick)}
            y2={y(tick)}
            stroke="#EFE8F8"
          />
          <text
            x={P.l - 8}
            y={y(tick) + 4}
            textAnchor="end"
            className="rp-axis"
          >
            {tick}
          </text>
        </g>
      ))}

      {series.map((s) => (
        <g key={s.name}>
          <path d={area(s.data)} fill={s.color} opacity="0.1" />
          <path
            d={path(s.data)}
            fill="none"
            stroke={s.color}
            strokeWidth="2.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {s.data.map((v, i) => (
            <circle
              key={i}
              cx={x(i)}
              cy={y(v)}
              r="3.5"
              fill="#fff"
              stroke={s.color}
              strokeWidth="2"
            >
              <title>{`${s.name} · ${labels[i]}: ${v}`}</title>
            </circle>
          ))}
        </g>
      ))}

      {labels.map((label, i) => (
        <text
          key={`${label}-${i}`}
          x={x(i)}
          y={H - 8}
          textAnchor="middle"
          className="rp-axis"
        >
          {label}
        </text>
      ))}
    </svg>
  );
}

function BarList({ items }) {
  if (!items.length) {
    return <p className="rp-empty">No job category data available.</p>;
  }

  const max = Math.max(1, ...items.map((item) => item.value));

  return (
    <div className="rp-bars">
      {items.map((item, i) => (
        <div className="rp-bar-row" key={item.name}>
          <div className="rp-bar-head">
            <span>{item.name}</span>
            <strong>{fmt(item.value)}</strong>
          </div>
          <div className="rp-bar-track">
            <div
              className="rp-bar-fill"
              style={{
                width: `${(item.value / max) * 100}%`,
                background: PALETTE[i % PALETTE.length],
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Donut({ items }) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  const R = 54;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="rp-donut-wrap">
      <svg
        viewBox="0 0 140 140"
        className="rp-donut"
        role="img"
        aria-label="Trainer status breakdown"
      >
        <circle
          cx="70"
          cy="70"
          r={R}
          fill="none"
          stroke="#F3EEFA"
          strokeWidth="18"
        />

        {total > 0 &&
          items.map((item, i) => {
            const length = (item.value / total) * C;
            const currentOffset = offset;
            offset += length;

            return (
              <circle
                key={item.name}
                cx="70"
                cy="70"
                r={R}
                fill="none"
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth="18"
                strokeDasharray={`${length} ${C - length}`}
                strokeDashoffset={-currentOffset}
                transform="rotate(-90 70 70)"
              >
                <title>{`${item.name}: ${item.value}`}</title>
              </circle>
            );
          })}

        <text x="70" y="66" textAnchor="middle" className="rp-donut-num">
          {fmt(total)}
        </text>
        <text x="70" y="84" textAnchor="middle" className="rp-donut-lbl">
          Trainers
        </text>
      </svg>

      <ul className="rp-legend">
        {items.map((item, i) => (
          <li key={item.name}>
            <i style={{ background: PALETTE[i % PALETTE.length] }} />
            <span>{item.name}</span>
            <strong>
              {total ? Math.round((item.value / total) * 100) : 0}%
            </strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Funnel({ items }) {
  const max = Math.max(1, items[0]?.value || 0);

  return (
    <div className="rp-funnel">
      {items.map((item, i) => (
        <div className="rp-funnel-row" key={item.name}>
          <div
            className="rp-funnel-bar"
            style={{
              width: `${Math.max(18, (item.value / max) * 100)}%`,
              background: PALETTE[i % PALETTE.length],
              color: i === 2 ? "#2B0C61" : "#fff",
            }}
          >
            {fmt(item.value)}
          </div>
          <span>
            {item.name}
            {i > 0 && max > 0 && (
              <em>
                {" "}
                · {Math.round((item.value / max) * 100)}% of applications
              </em>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function Reports() {
  const [range, setRange] = useState("30d");
  const [records, setRecords] = useState({
    partners: [],
    applications: [],
    interviews: [],
    trainers: [],
    jobs: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setErrors({});

    const results = await Promise.all(
      Object.entries(API_ENDPOINTS).map(async ([key, url]) => {
        try {
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const json = await response.json();

          return {
            key,
            records: getList(json),
            error: null,
          };
        } catch (error) {
          return {
            key,
            records: [],
            error: error.message || "Request failed",
          };
        }
      })
    );

    const nextRecords = {};
    const nextErrors = {};

    results.forEach(({ key, records: list, error }) => {
      nextRecords[key] = list;
      if (error) nextErrors[key] = error;
    });

    setRecords(nextRecords);
    setErrors(nextErrors);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const data = useMemo(() => {
    const now = Date.now();
    const cutoff = now - RANGES[range].days * 86400000;

    const applications = records.applications.filter((item) => {
      const date = getDate(item);
      return date && date.getTime() >= cutoff && date.getTime() <= now;
    });

    const countStatus = (status) =>
      applications.filter((item) => getStatus(item) === status).length;

    const selected = applications.filter((item) =>
      ["SELECTED", "PLACED"].includes(getStatus(item))
    ).length;

    
const categoriesMap = records.jobs.reduce((counts, job) => {
  const category = String(
    job.jobTitle || "Uncategorized"
  ).trim();

  counts[category] = (counts[category] || 0) + 1;
  return counts;
}, {});

const categories = Object.entries(categoriesMap)
  .map(([name, value]) => ({ name, value }))
  .sort((a, b) => b.value - a.value)
  .slice(0, 5);


    
const trainerStatusKeys = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "BLOCKED",
  "INACTIVE",
];
   
const trainerStatus = trainerStatusKeys.map((status) => ({
  name: status,
  value: records.trainers.filter(
    (trainer) =>
      String(trainer.status || "").trim().toUpperCase() === status
  ).length,
}));


    const topPartnerMap = {};

    applications.forEach((application) => {
      const name =
        application.companyName ||
        application.partnerName ||
        "Unknown partner";

      if (!topPartnerMap[name]) {
        topPartnerMap[name] = {
          name,
          applications: 0,
          placements: 0,
        };
      }

      topPartnerMap[name].applications += 1;

      if (["SELECTED", "PLACED"].includes(getStatus(application))) {
        topPartnerMap[name].placements += 1;
      }
    });

    const topPartners = Object.values(topPartnerMap)
      .sort((a, b) => b.applications - a.applications)
      .slice(0, 5);

    const labels =
      range === "7d"
        ? ["6d ago", "5d ago", "4d ago", "3d ago", "2d ago", "Yesterday", "Today"]
        : range === "30d"
        ? ["Week 1", "Week 2", "Week 3", "Week 4"]
        : range === "90d"
        ? ["Month 1", "Month 2", "Month 3"]
        : ["M1", "M2", "M3", "M4", "M5", "M6", "M7", "M8", "M9", "M10", "M11", "M12"];

    const trendApplications = labels.map((_, index) => {
      const start = cutoff + (index / labels.length) * (now - cutoff);
      const end = cutoff + ((index + 1) / labels.length) * (now - cutoff);

      return applications.filter((item) => {
        const date = getDate(item)?.getTime();
        return date != null && date >= start && date < end;
      }).length;
    });

    // Count placements by the same time bucket using the application date.
    const trendPlacements = labels.map((_, index) => {
      const start = cutoff + (index / labels.length) * (now - cutoff);
      const end = cutoff + ((index + 1) / labels.length) * (now - cutoff);

      return applications.filter((item) => {
        const date = getDate(item)?.getTime();
        return (
          date != null &&
          date >= start &&
          date < end &&
          ["SELECTED", "PLACED"].includes(getStatus(item))
        );
      }).length;
    });

    return {
      kpis: [
        {
          id: "trainers",
          label: "Total Trainers",
          value: records.trainers.length,
          icon: Users,
        },
        {
          id: "partners",
          label: "Approved Partners",
          value: records.partners.filter(
            (partner) => getStatus(partner) === "APPROVED"
          ).length,
          icon: Building2,
        },
        {
          id: "jobs",
          label: "Jobs Posted",
          value: records.jobs.length,
          icon: BriefcaseBusiness,
        },
        {
          id: "apps",
          label: "Applications",
          value: applications.length,
          icon: ClipboardList,
        },
        {
          id: "placed",
          label: "Selected / Placed",
          value: selected,
          icon: CheckCircle,
        },
        {
          id: "revenue",
          label: "Revenue",
          value: null,
          icon: CircleDollarSign,
          money: true,
        },
      ],
      trend: {
        labels,
        applications: trendApplications,
        placements: trendPlacements,
      },
      categories,
      trainerStatus,
      topPartners,
      funnel: [
        { name: "Applications", value: applications.length },
        { name: "Shortlisted", value: countStatus("SHORTLISTED") },
        {
          name: "Interview stage",
          value: countStatus("INTERVIEW"),
        },
        { name: "Selected / Placed", value: selected },
      ],
    };
  }, [records, range]);
  

  const exportCsv = () => {
    const rows = [
      ["Partner", "Applications", "Placements", "Placement Rate"],
      ...data.topPartners.map((partner) => [
        partner.name,
        partner.applications,
        partner.placements,
        partner.applications
          ? `${Math.round(
              (partner.placements / partner.applications) * 100
            )}%`
          : "0%",
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `certed-report-${range}.csv`;
    anchor.click();

    URL.revokeObjectURL(url);
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="rp-page">
      <style>{`
        .rp-page {
          --purple:#3E0F8D;
          --light-purple:#9564DD;
          --gold:#E4DA72;
          --text:#2B0C61;
          --muted:#716984;
          --border:#E7DEF3;
          padding:28px;
          background:#F8F6FC;
          min-height:100vh;
          color:var(--text);
          font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        }
        .rp-page * { box-sizing:border-box; }
        .rp-head { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:22px; }
        .rp-head h1 { margin:0; font-size:26px; font-weight:800; letter-spacing:-.4px; color:var(--purple); }
        .rp-head p { margin:4px 0 0; color:var(--muted); font-size:13px; }
        .rp-actions { display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
        .rp-tabs { display:inline-flex; background:#fff; border:1px solid var(--border); border-radius:10px; padding:3px; }
        .rp-tabs button { border:0; background:transparent; padding:8px 14px; border-radius:8px; font:inherit; font-size:12px; font-weight:700; color:var(--muted); cursor:pointer; }
        .rp-tabs button.on { background:var(--purple); color:#fff; }
        .rp-export,.rp-refresh { display:inline-flex; align-items:center; gap:8px; border:1px solid var(--border); background:#fff; color:var(--purple); font:inherit; font-size:12px; font-weight:800; padding:9px 14px; border-radius:10px; cursor:pointer; }
        .rp-export:hover,.rp-refresh:hover { background:var(--purple); color:#fff; }
        .rp-refresh:disabled { opacity:.6; cursor:wait; }
        .rp-kpis { display:grid; grid-template-columns:repeat(6,minmax(0,1fr)); gap:14px; margin-bottom:18px; }
        .rp-kpi { background:#fff; border:1px solid var(--border); border-radius:14px; padding:16px; position:relative; overflow:hidden; min-width:0; }
        .rp-kpi::after { content:""; position:absolute; left:0; top:0; width:100%; height:3px; background:linear-gradient(90deg,var(--purple),var(--light-purple)); }
        .rp-kpi-ico { width:34px; height:34px; border-radius:9px; background:#F2EBFA; color:var(--purple); display:flex; align-items:center; justify-content:center; margin-bottom:12px; }
        .rp-kpi-lbl { color:var(--muted); font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.35px; }
        .rp-kpi-val { font-size:24px; font-weight:800; margin:6px 0 8px; overflow-wrap:anywhere; }
        .rp-delta { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:800; padding:4px 8px; border-radius:20px; background:#F2EBFA; color:var(--purple); }
        .rp-grid { display:grid; gap:18px; margin-bottom:18px; }
        .rp-grid.g-2-1 { grid-template-columns:2fr 1fr; }
        .rp-grid.g-1-1 { grid-template-columns:1fr 1fr; }
        .rp-card { background:#fff; border:1px solid var(--border); border-radius:14px; padding:20px; min-width:0; }
        .rp-card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; gap:10px; flex-wrap:wrap; }
        .rp-card-head h3 { margin:0; font-size:15px; font-weight:800; }
        .rp-card-head small { color:var(--muted); font-size:12px; display:block; margin-top:4px; }
        .rp-key { display:flex; gap:14px; flex-wrap:wrap; font-size:12px; color:var(--muted); font-weight:600; }
        .rp-key i { display:inline-block; width:10px; height:10px; border-radius:50%; margin-right:6px; }
        .rp-svg { width:100%; height:auto; display:block; }
        .rp-axis { font-size:11px; fill:var(--muted); }
        .rp-bars { display:flex; flex-direction:column; gap:14px; }
        .rp-bar-head { display:flex; justify-content:space-between; gap:8px; font-size:12px; margin-bottom:6px; font-weight:600; }
        .rp-bar-track { height:8px; background:#F3EEFA; border-radius:8px; overflow:hidden; }
        .rp-bar-fill { height:100%; border-radius:8px; transition:width .3s ease; }
        .rp-donut-wrap { display:flex; align-items:center; gap:22px; flex-wrap:wrap; justify-content:center; }
        .rp-donut { width:160px; height:160px; max-width:100%; }
        .rp-donut-num { font-size:22px; font-weight:800; fill:var(--text); }
        .rp-donut-lbl { font-size:10px; fill:var(--muted); font-weight:700; }
        .rp-legend { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; min-width:130px; }
        .rp-legend li { display:flex; align-items:center; gap:8px; font-size:12px; font-weight:600; }
        .rp-legend li i { width:10px; height:10px; flex-shrink:0; border-radius:3px; }
        .rp-legend li strong { margin-left:auto; }
        .rp-funnel { display:flex; flex-direction:column; gap:12px; }
        .rp-funnel-row { display:flex; flex-direction:column; gap:5px; }
        .rp-funnel-bar { padding:9px 12px; border-radius:8px; font-size:13px; font-weight:800; min-width:18%; }
        .rp-funnel-row span { font-size:12px; color:var(--muted); font-weight:600; }
        .rp-funnel-row em { font-style:normal; color:var(--light-purple); }
        .rp-table-wrap { overflow-x:auto; }
        .rp-table { width:100%; border-collapse:collapse; font-size:12px; }
        .rp-table th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.4px; color:var(--muted); padding:10px 12px; border-bottom:1px solid var(--border); white-space:nowrap; }
        .rp-table td { padding:13px 12px; border-bottom:1px solid #F3EEFA; white-space:nowrap; }
        .rp-table tr:last-child td { border-bottom:none; }
        .rp-table tbody tr:hover { background:#FAF7FE; }
        .rp-table .num { text-align:right; }
        .rp-rank { display:inline-flex; width:24px; height:24px; border-radius:50%; align-items:center; justify-content:center; background:#F2EBFA; color:var(--purple); font-size:11px; font-weight:800; margin-right:10px; }
        .rp-table tr:first-child .rp-rank { background:var(--gold); color:var(--text); }
        .rp-rate { display:inline-block; padding:3px 9px; border-radius:20px; background:#F2EBFA; color:var(--purple); font-weight:800; font-size:11px; }
        .rp-warning { display:flex; gap:10px; padding:14px; margin-bottom:18px; border:1px solid #F0D9A3; border-radius:10px; background:#FFF8E8; color:#80570D; font-size:12px; }
        .rp-warning p { margin:6px 0; overflow-wrap:anywhere; }
        .rp-empty { color:var(--muted); font-size:12px; padding:14px 0; }
        .rp-footer { display:flex; justify-content:space-between; flex-wrap:wrap; gap:8px; margin-top:16px; color:var(--muted); font-size:11px; }
        @media(max-width:1280px) { .rp-kpis { grid-template-columns:repeat(3,minmax(0,1fr)); } }
        @media(max-width:992px) { .rp-grid.g-2-1,.rp-grid.g-1-1 { grid-template-columns:1fr; } }
        @media(max-width:768px) { .rp-page { padding:16px; } .rp-kpis { grid-template-columns:repeat(2,minmax(0,1fr)); } .rp-kpi-val { font-size:20px; } }
        @media(max-width:420px) { .rp-kpis { grid-template-columns:1fr; } .rp-tabs button { padding:7px 9px; } }
      `}</style>

      <div className="rp-head">
        <div>
          <h1>Reports &amp; Analytics</h1>
          <p>CertED platform overview · {RANGES[range].label}</p>
        </div>

        <div className="rp-actions">
          <div className="rp-tabs" role="tablist" aria-label="Report date range">
            {Object.keys(RANGES).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={range === key}
                className={range === key ? "on" : ""}
                onClick={() => setRange(key)}
              >
                {key === "12m" ? "12M" : key.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="rp-refresh"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw size={14} />
            {loading ? "Loading..." : "Refresh"}
          </button>

          <button type="button" className="rp-export" onClick={exportCsv}>
            <Download size={15} />
            Export CSV
          </button>
        </div>
      </div>

      {hasErrors && (
        <div className="rp-warning" role="alert">
          <AlertCircle size={18} />
          <div>
            <strong>Some API requests failed.</strong>
            {Object.entries(errors).map(([name, message]) => (
              <p key={name}>
                {name}: {message}
              </p>
            ))}
            <span>
              Check that Spring Boot is running, the endpoints are correct,
              and CORS permits requests from your React app.
            </span>
          </div>
        </div>
      )}

      <div className="rp-kpis">
        {data.kpis.map((kpi) => {
          const Icon = kpi.icon;

          return (
            <div className="rp-kpi" key={kpi.id}>
              <div className="rp-kpi-ico">
                <Icon size={17} />
              </div>
              <div className="rp-kpi-lbl">{kpi.label}</div>
              <div className="rp-kpi-val">
                {loading && !errors[kpi.id] && kpi.value === 0
                  ? "..."
                  : kpi.value == null
                  ? "—"
                  : fmt(kpi.value)}
              </div>
              <span className="rp-delta">
                {kpi.value == null
                  ? "API not connected"
                  : errors[
                      kpi.id === "trainers"
                        ? "trainers"
                        : kpi.id === "partners"
                        ? "partners"
                        : kpi.id === "jobs"
                        ? "jobs"
                        : "applications"
                    ]
                  ? "API unavailable"
                  : "Current API data"}
              </span>
            </div>
          );
        })}
      </div>

      <div className="rp-grid g-2-1">
        <div className="rp-card">
          <div className="rp-card-head">
            <div>
              <h3>Applications vs Placements</h3>
              <small>Applications grouped by application date</small>
            </div>
            <div className="rp-key">
              <span><i style={{ background: "#3E0F8D" }} />Applications</span>
              <span><i style={{ background: "#9564DD" }} />Selected / Placed</span>
            </div>
          </div>
          <TrendChart
            labels={data.trend.labels}
            series={[
              {
                name: "Applications",
                data: data.trend.applications,
                color: "#3E0F8D",
              },
              {
                name: "Selected / Placed",
                data: data.trend.placements,
                color: "#9564DD",
              },
            ]}
          />
        </div>

        <div className="rp-card">
          <div className="rp-card-head">
            <div>
              <h3>Jobs by Category</h3>
              <small>Top 5 categories from job records</small>
            </div>
          </div>
          <BarList items={data.categories} />
        </div>
      </div>

      <div className="rp-grid g-1-1">
        <div className="rp-card">
          <div className="rp-card-head">
            <div>
              <h3>Trainer Status</h3>
              <small>Based on trainer status fields</small>
            </div>
          </div>
          <Donut items={data.trainerStatus} />
        </div>

        <div className="rp-card">
          <div className="rp-card-head">
            <div>
              <h3>Hiring Funnel</h3>
              <small>Application status breakdown</small>
            </div>
          </div>
          <Funnel items={data.funnel} />
        </div>
      </div>

      <div className="rp-card">
        <div className="rp-card-head">
          <div>
            <h3>Top Partners</h3>
            <small>Ranked by applications in the selected period</small>
          </div>
        </div>

        <div className="rp-table-wrap">
          <table className="rp-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th className="num">Applications</th>
                <th className="num">Placements</th>
                <th className="num">Placement Rate</th>
                <th className="num">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.topPartners.map((partner, index) => (
                <tr key={partner.name}>
                  <td>
                    <span className="rp-rank">{index + 1}</span>
                    {partner.name}
                  </td>
                  <td className="num">{fmt(partner.applications)}</td>
                  <td className="num">{fmt(partner.placements)}</td>
                  <td className="num">
                    <span className="rp-rate">
                      {partner.applications
                        ? Math.round(
                            (partner.placements / partner.applications) * 100
                          )
                        : 0}
                      %
                    </span>
                  </td>
                  <td className="num">—</td>
                </tr>
              ))}

              {!loading && data.topPartners.length === 0 && (
                <tr>
                  <td colSpan="5" className="rp-empty">
                    No application records found for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rp-footer">
        <span>
          {lastUpdated
            ? `Last updated: ${lastUpdated.toLocaleString("en-IN")}`
            : "Waiting for API data"}
        </span>
        <span>
          {records.partners.length} partners · {records.trainers.length} trainers
          {" · "}
          {records.jobs.length} jobs · {records.interviews.length} interviews
        </span>
      </div>
    </div>
  );
}

export default Reports;
